/**
 * Modo de liquidação: o que o pagamento faz com o TÍTULO no settlement.
 *
 * `total_payment` é tri-state e não é status: é o sinal que vai na movimentação.
 *   true  → liquida o título, mesmo que o valor pago seja menor
 *   false → mantém aberto, mesmo que o valor pago seja maior
 *   null  → OPERATION_WATERFALL: o valor entra na OPERAÇÃO e desce em cascata pelas parcelas
 *           abertas, em ordem — cada uma coberta liquida e o resto transborda para a seguinte.
 *           É o que o time chama de "baldinho"; o identificador é em inglês, o rótulo de tela
 *           segue em português ("Amortizar Operação").
 *
 * Qual modo é válido depende do CENÁRIO do charge, e oferecer o errado gera movimentação errada.
 * Esta tabela espelha `billing/src/domain/charge_scenario.ALLOWED_MODES` — mudou lá, muda aqui.
 * Vive num módulo próprio porque três telas precisam dela (drill-in de faturas, geração de
 * fatura e Registrar Recebimento) e cópias divergem em silêncio.
 */
export type LiquidationMode = 'TOTAL' | 'PARTIAL' | 'OPERATION_WATERFALL';

export const MODE_TO_TOTAL_PAYMENT: Record<LiquidationMode, boolean | null> = {
    TOTAL: true, PARTIAL: false, OPERATION_WATERFALL: null,
};

export const MODE_LABELS: Record<LiquidationMode, string> = {
    TOTAL: 'Liquida o título',
    PARTIAL: 'Mantém o título aberto',
    OPERATION_WATERFALL: 'Amortiza a operação (distribui pelas parcelas)',
};

/** Modos aceitos ao PAGAR uma fatura. GUARANTEE aceita só total — garantia não é parcial. */
export const PAYMENT_MODES_BY_SCENARIO: Record<string, LiquidationMode[]> = {
    RECEIVABLE: ['TOTAL', 'PARTIAL'],
    INSTALLMENT: ['TOTAL', 'PARTIAL', 'OPERATION_WATERFALL'],
    GUARANTEE: ['TOTAL'],
};

/**
 * Modos aceitos ao GERAR fatura. Difere do pagamento em GUARANTEE: garantia não gera fatura pela
 * tela (só nasce paga, via REGISTER_PAID_INVOICE), então a lista é vazia.
 */
export const DERIVE_MODES_BY_SCENARIO: Record<string, LiquidationMode[]> = {
    RECEIVABLE: ['TOTAL', 'PARTIAL'],
    INSTALLMENT: ['TOTAL', 'PARTIAL', 'OPERATION_WATERFALL'],
    GUARANTEE: [],
};

/** De volta ao modo, para pré-selecionar o que a fatura já declarava. */
export const modeOf = (totalPayment: boolean | null | undefined): LiquidationMode => (
    totalPayment === null ? 'OPERATION_WATERFALL' : totalPayment === false ? 'PARTIAL' : 'TOTAL'
);

/** O modo pedido, se o cenário aceitar; senão o primeiro que ele aceita. */
export const clampMode = (wanted: LiquidationMode, allowed: LiquidationMode[]): LiquidationMode => (
    allowed.includes(wanted) ? wanted : (allowed[0] || 'TOTAL')
);
